import jsonExport from "jsonexport/dist";
import { downloadCSV } from "react-admin";
import { formatTimestamp } from "../../../lib/dateFormat";
import { requestTypeAdminLabels } from "../../../lib/constants";

const formatUser = (user) => {
  if (!user) {
    return "";
  }
  return `${user.firstName} ${user.lastName} (${user.email})`;
};

// transformContractRequest takes a contract request and breaks it out into individual contracts
function transformContractRequest(contractRequest, carriers, agencies, users, specialists) {
  const {
    id,
    npn,
    firstName,
    lastName,
    preferredName,
    phone,
    email,
    address,
    city,
    state,
    zip,
    ssn,
    dob,
    fsd,
    payment,
    paymentNpn,
    paymentCorporation,
    paymentTaxId,
    paymentUpline,
    notes,
    requestType,
    user: userId,
    specialist: specialistId,
    specialistName,
    agent,
    agency,
    emailRecipients,
    unknownAgent,
    commissionEmail,
    enrollmentEmail,
    createdAt,
    updatedAt,
  } = contractRequest;
  const emailRecipientList = emailRecipients.join("; ");
  const user = users[userId];
  const specialist = specialists[specialistId]
  const userDescription = user ? `${user.firstName} ${user.lastName} (${user.email})` : "Unknown";
  const unknownAgentText = (unknownAgent) ? "Yes" : "No";

  return contractRequest.contracts.map((contract) => {
    const carrierStatesText = contract.carrierStates ? contract.carrierStates.join(", ") : "";
    const withReleaseText = contract.withRelease ? "Yes" : "No";

    return {
      "Contract Request ID": id,
      // #106 - which kind of request each exported row is (same labels as the
      // list's Type column).
      "Request Type": requestTypeAdminLabels[requestType] || requestType || "",
      "Carrier Name": carriers[contract.carrier]?.value || "Unknown",
      "Agent NPN": npn,
      "Agent First Name": firstName,
      "Agent Last Name": lastName,
      "Agent Preferred Name": preferredName,
      "Agent Email": email,
      "Agent Phone": phone,
      "Agent Address": address,
      "Agent City": city,
      "Agent State": state,
      "Agent ZIP": zip,
      // #113 - requested report additions. SSN reaches the browser for
      // admins only (the server strips it for everyone else), so non-admin
      // exports have the column blank.
      "Agent Date of Birth": dob || "",
      "Agent SSN": ssn || "",
      "Agent in database": agent ? "Yes" : "No",
      "Unknown Agent": unknownAgentText,
      "Payment Option": payment,
      "Payment NPN": paymentNpn,
      "Payment Corporation": paymentCorporation,
      "Payment Tax ID": paymentTaxId,
      "Payment Upline": paymentUpline,
      "Commission Email": commissionEmail,
      "Enrollment Email": enrollmentEmail,
      "Policy Entity": contract.policyEntity,
      "With Release": withReleaseText,
      "Policy Creation": contract.policyCreation,
      "Carrier States": carrierStatesText,
      "Carrier Type": contract.carrierType,
      "Carrier Level": contract.carrierLevel,
      "Contract Notes": contract.notes,
      "FSD": fsd || "",
      "ACE": specialistName || formatUser(specialist),
      "Agency": agencies[agency]?.name || "N/A",
      "PURL": agencies[agency]?.domain || "N/A",
      "Submitted By": userDescription,
      "Email Recipients": emailRecipientList,
      "Request Notes": notes,
      "Request Created": formatTimestamp(createdAt),
      "Request Updated": formatTimestamp(updatedAt),
      "Contract Created": formatTimestamp(contract.createdAt),
      "Contract Updated": formatTimestamp(contract.updatedAt),
    };
  });
}

const headers = [
  "Contract Request ID",
  "Request Type",
  "Carrier Name",
  "Agent NPN",
  "Agent First Name",
  "Agent Last Name",
  "Agent Preferred Name",
  "Agent Email",
  "Agent Phone",
  "Agent Address",
  "Agent City",
  "Agent State",
  "Agent ZIP",
  "Agent Date of Birth",
  "Agent SSN",
  "Agent in database",
  "Unknown Agent",
  "Payment Option",
  "Payment NPN",
  "Payment Corporation",
  "Payment Tax ID",
  "Payment Upline",
  "Commission Email",
  "Enrollment Email",
  "Policy Entity",
  "With Release",
  "Policy Creation",
  "Carrier States",
  "Carrier Type",
  "Carrier Level",
  "Contract Notes",
  "FSD",
  "ACE",
  "Agency",
  "PURL",
  "Submitted By",
  "Email Recipients",
  "Request Notes",
  "Request Created",
  "Request Updated",
  "Contract Created",
  "Contract Updated",
];

const exporter = async (records, fetchRelatedRecords) => {
  const agencies = await fetchRelatedRecords(records, "agency", "agency");
  // One flat list of contracts, so the carrier ids on each are found.
  const contracts = records.flatMap((cr) => cr.contracts || []);
  const carriers = await fetchRelatedRecords(contracts, "carrier", "carrier");
  const users = await fetchRelatedRecords(records, "user", "user");
  const specialists = await fetchRelatedRecords(records, "specialist", "user");

  const data = records.map((cr) =>
    transformContractRequest(cr, carriers, agencies, users, specialists)
  );
  const report = await jsonExport(data, { headers });
  return downloadCSV(report, "Contract Requests");
};

export default exporter;
